import { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import Flex from 'legacyfrontend/components/Flex';
import FlightDetailDialog from './FlightDetailDialog';
import TooltipLuggage from './TooltipLuggage';
import formatSecondsToHours from 'legacyfrontend/utils/formatSecondsToHours';
import { formatDate, formatTime } from 'legacyfrontend/utils/dateUtils';
import TooltipScale from './TooltipScale';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
const checkboxStyles = { margin: 0, padding: 0 };

const FlightInfoRow = memo(({ flight, kind, isSelected, onSelect, isBookingPage = false }) => {
  const { background, gray, coral } = useListingColors();
  const [scales, setScales] = useState([]);

  const flightData = useMemo(() => {
    const isMultiFlight = Array.isArray(flight) && flight.length > 0;
    const firstFlight = isMultiFlight ? flight[0] : flight;
    const lastFlight = isMultiFlight ? flight[flight.length - 1] : flight;

    return {
      departureDate: firstFlight.departureDate,
      arrivalDate: lastFlight.arrivalDate,
      departureCity: firstFlight.departure.city,
      arrivalCity: lastFlight.arrival.city,
      hasScales: isMultiFlight && flight.length > 1,
      originId: firstFlight.originDestinationReferences.slice(0, 3),
      destinationId: firstFlight.originDestinationReferences.slice(3, 6),
      duration: firstFlight.duration,
    };
  }, [flight]);

  const baggage = useMemo(() => [...new Set(flight.map((flight) => flight.baggage))], [flight]);

  const airlineLogos = useMemo(() => {
    return [...new Set(flight.map((flight) => flight.logoCompany))].filter(Boolean);
  }, [flight]);

  const calculateScales = useCallback((flights) => {
    if (!Array.isArray(flights) || flights.length <= 1) {
      setScales([{ isNotScale: true }, { isNotScale: true }]);
      return;
    }

    const newScales = [{ isNotScale: true }];

    flights.slice(0, -1).forEach((currentFlight, index) => {
      const nextFlight = flights[index + 1];
      const waitTime = Math.max(
        (new Date(nextFlight.departureDate) - new Date(currentFlight.arrivalDate)) / 1000,
        0
      );

      newScales.push({
        id: `scale${index + 1}`,
        origin: `${currentFlight.arrival.name}, ${currentFlight.arrival.city}`,
        destination: `${nextFlight.arrival.name}, ${nextFlight.arrival.city}`,
        samePlane: false,
        technicalStop: false,
        sameAirport: currentFlight.arrival.name === nextFlight.departure.name,
        waitTime,
        isNotScale: false,
      });
    });

    newScales.push({ isNotScale: true });
    setScales(newScales);
  }, []);

  useEffect(() => {
    calculateScales(flight);
  }, [flight, calculateScales]);

  return (
    <Stack
      className='flight_card__data'
      sx={{
        alignItems: 'stretch',
        flexDirection: 'row',
      }}
    >
      {!isBookingPage && (
        <div
          className='flight_card_info_first_row'
          style={{
            borderLeft: `10px solid ${isSelected ? background : gray}`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected}
                onChange={onSelect}
                sx={{
                  paddingLeft: '1rem',
                  '&.Mui-checked': {
                    color: background,
                  },
                }}
              />
            }
            sx={checkboxStyles}
          />
        </div>
      )}

      <Stack
        className='flight_card_content_column'
        sx={{
          alignItems: { xs: 'stretch', lg: 'center' },
          flex: 1,
          flexDirection: { xs: 'column', lg: 'row' },
          minWidth: 0,
        }}
      >
        <Stack
          className='flight_card_info_second_row'
          sx={{
            flexDirection: { xs: 'column', lg: 'row' },
            marginTop: 0,
            ...(isBookingPage && {
              pl: 2,
            }),
          }}
        >
          {/* Airline Logos */}
          {airlineLogos.length > 0 && (
            <Stack
              sx={{
                alignItems: { xs: 'center', lg: 'flex-start' },
                alignSelf: { xs: 'center', lg: 'auto' },
                flexDirection: { xs: 'row', lg: 'column' },
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                gap: { xs: 1.5, lg: 0 },
                marginRight: { xs: 0, lg: 2 },
                marginBottom: { xs: 2, lg: 0 },
                paddingLeft: { xs: 1.5, sm: 2, lg: 0 },
                justifyContent: { xs: 'flex-start', lg: 'center' },
                width: { xs: '100%', lg: 'auto' },
              }}
            >
              {airlineLogos.map((logoUrl, index) => (
                <Box
                  alt='Airline Logo'
                  component='img'
                  key={`logo-${index}`}
                  src={logoUrl}
                  sx={{
                    height: 'auto',
                    marginTop: { xs: 0, lg: index > 0 ? '1rem' : 0 },
                    maxWidth: '100px',
                    width: { xs: 'clamp(78px, 28vw, 112px)', lg: '100%' },
                  }}
                />
              ))}
            </Stack>
          )}

        <Stack
          className='flight_card_info'
          sx={{
            flexDirection: 'column',
            paddingLeft: '1rem',
            paddingRight: '1rem',
          }}
        >
          <Flex className='flight_card_data_jcsb'>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.departureDate)}
            </Typography>
            <Typography color={coral} fontSize='0.75rem'>
              {!flightData.hasScales
                ? 'Vuelo Directo'
                : `${scales.length - 2} ${scales.length > 3 ? 'escalas' : 'escala'}`}
            </Typography>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.arrivalDate)}
            </Typography>
          </Flex>

          <Flex
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <div className='dashed-line' />
            <div
              style={{
                display: 'flex',
                flex: 1,
                justifyContent: 'space-between',
                position: 'relative',
              }}
            >
              {scales.map((scale, index) => (
                <TooltipScale escala={scale} isNotScale={scale.isNotScale} key={`scale-${index}`} />
              ))}
            </div>
          </Flex>

          <Flex className='flight_card_data_jcsb'>
            <p className='bottom_label_flight'>{flightData.originId}</p>
            <p className='bottom_label_flight'>{flightData.destinationId}</p>
          </Flex>

          <Flex className='flight_card_data_jcsb' style={{ alignItems: 'flex-start' }}>
            <p className='location_text'>{flightData.departureCity}</p>
            <p className='location_text'>{flightData.arrivalCity}</p>
          </Flex>
        </Stack>
      </Stack>
      <Stack
        className='flight_card_info_three_row'
        sx={{
          alignItems: 'center',
          alignSelf: { xs: 'stretch', lg: 'center' },
          flexDirection: { xs: 'row', lg: 'row' },
          gap: { xs: 1, lg: 0 },
          height: 'auto',
          minHeight: { xs: 40, lg: 'auto' },
          paddingBottom: { xs: 2, lg: 0 },
          paddingTop: { xs: 1, lg: 'inherit' },
          minWidth: { xs: '100%', lg: 'auto' },
          justifyContent: { xs: 'space-between', lg: 'center' },
          paddingLeft: { xs: 2, sm: 3, lg: '1rem' },
          paddingRight: { xs: 2, sm: 3, lg: '1rem' },
          textAlign: 'center',
        }}
      >
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'
          sx={{ display: { xs: 'flex', lg: 'none' }, width: '100%' }}
        >
          <Stack className='flight_card_duration_row' direction='row' gap={0.5}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
              Duración:
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300, lineHeight: 1, whiteSpace: 'nowrap' }}>
              {formatSecondsToHours(flightData.duration)}
            </Typography>
          </Stack>

          <Stack alignItems='center' direction='row' gap={1} justifyContent='center'>
            <TooltipLuggage checkedBag={baggage[0]} />
            <Stack
              alignItems='center'
              className='flight_card_info_four_row'
              justifyContent='center'
              sx={{ padding: 0 }}
            >
              <FlightDetailDialog
                destinationId={flightData.destinationId}
                flight={flight}
                kind={kind}
                originId={flightData.originId}
                scales={scales}
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack flex={1} gap={1} justifyContent='center' sx={{ display: { xs: 'none', lg: 'flex' }, height: 'auto' }}>
          <TooltipLuggage checkedBag={baggage[0]} />
          <Stack flexDirection='column' textAlign='center'>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>Duración:</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>
              {formatSecondsToHours(flightData.duration)}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          sx={{
            alignItems: 'center',
            display: { xs: 'none', lg: 'flex' },
            justifyContent: 'center',
            padding: 2,
          }}
        >
          <FlightDetailDialog
            destinationId={flightData.destinationId}
            flight={flight}
            kind={kind}
            originId={flightData.originId}
            scales={scales}
          />
        </Stack>
      </Stack>
      </Stack>
    </Stack>
  );
});

export default FlightInfoRow;
